import { AlertCircle, File, FileSpreadsheet, FileText, Loader2, X } from "lucide-react";
import { cn } from "~/lib/utils";

const SPREADSHEET_EXTENSIONS = new Set(["csv", "tsv", "xls", "xlsx"]);
const TEXT_ICON_EXTENSIONS = new Set([
  "txt",
  "md",
  "json",
  "py",
  "r",
  "sql",
  "yaml",
  "yml",
  "log",
  "xml",
  "html",
]);

function extension(filename: string): string | null {
  const dot = filename.lastIndexOf(".");
  if (dot < 0 || dot === filename.length - 1) return null;
  return filename.slice(dot + 1).toLowerCase();
}

// Second line of a file card: the uppercased extension, or a neutral fallback.
function typeLabel(filename: string): string {
  return extension(filename)?.toUpperCase() ?? "FILE";
}

function iconFor(filename: string) {
  const ext = extension(filename);
  if (ext && SPREADSHEET_EXTENSIONS.has(ext)) return FileSpreadsheet;
  if (ext && TEXT_ICON_EXTENSIONS.has(ext)) return FileText;
  return File;
}

// Footprint of a sent-bubble file card. Narrow enough that four fit across the
// bubble's max width; exported so a loading placeholder reserves the same box.
// ImageView's "thumbnail" variant clamps to this width too — keep them in step.
export const SENT_CARD_SIZE = "h-14 w-40";

interface AttachmentCardProps {
  filename: string;
  // Set for images → renders a thumbnail (object URL in the composer, presigned
  // preview URL on a sent bubble). Absent → a two-line file card.
  imageUrl?: string;
  // "sent" is the static bubble card: no type icon, smaller box. "tray" is the
  // composer's, where the icon slot also carries upload status.
  variant?: "tray" | "sent";
  // Composer-tray adornments. Omitted on a sent bubble, which is static.
  status?: "ready" | "uploading" | "done" | "error";
  errorTitle?: string;
  // When provided, a floating remove button is shown in the top-right corner.
  onRemove?: () => void;
}

// Compact, action-free presentation of one attachment, shared by the composer
// tray and the sent user bubble. Images render as a small square thumbnail;
// everything else as a filename + type card.
export const AttachmentCard = ({
  filename,
  imageUrl,
  variant = "tray",
  status,
  errorTitle,
  onRemove,
}: AttachmentCardProps) => {
  const uploading = status === "uploading";
  const errored = status === "error";

  const removeButton = onRemove && (
    <button
      type="button"
      onClick={onRemove}
      aria-label={`Remove ${filename}`}
      className="absolute -right-1.5 -top-1.5 cursor-pointer rounded-full bg-background p-0.5 text-muted-foreground shadow-sm ring-1 ring-border hover:text-foreground"
    >
      <X className="size-3" aria-hidden />
    </button>
  );

  if (imageUrl) {
    return (
      <div className="relative">
        <div
          className={cn(
            "size-16 overflow-hidden rounded-lg border",
            errored ? "border-destructive/50" : "border-border"
          )}
        >
          <img src={imageUrl} alt={filename} className="size-full object-cover" />
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/60">
              <Loader2 className="size-4 animate-spin" aria-hidden />
            </div>
          )}
          {errored && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-destructive/25 text-destructive"
              title={errorTitle}
            >
              <AlertCircle className="size-4" aria-hidden />
            </div>
          )}
        </div>
        {removeButton}
      </div>
    );
  }

  const Icon = uploading ? Loader2 : errored ? AlertCircle : iconFor(filename);
  const sent = variant === "sent";

  return (
    <div className="relative">
      <div
        className={cn(
          "flex items-center gap-2.5 rounded-lg border px-3",
          // A tray card's h-16 matches the image thumbnail's size-16 so both
          // align in a row; sent cards have a grid to themselves.
          sent ? SENT_CARD_SIZE : "h-16 w-52",
          errored ? "border-destructive/50 bg-destructive/5" : "border-border bg-muted/40"
        )}
      >
        {!sent && (
          <Icon
            className={cn(
              "size-7 shrink-0",
              uploading && "animate-spin",
              errored ? "text-destructive" : "text-muted-foreground"
            )}
            aria-hidden
          />
        )}
        {/* pr-3 keeps a tray card's text clear of its remove button. */}
        <div className={cn("min-w-0 flex-1", !sent && "pr-3")}>
          <p className="truncate text-xs font-medium" title={errorTitle ?? filename}>
            {filename}
          </p>
          <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            {/* A sent card pairs the icon with the type label at label size: the
                glyphs aren't distinguishable this small, so it reads as one bit
                of metadata rather than a second, redundant type signal. */}
            {sent && <Icon className="size-3 shrink-0" aria-hidden />}
            {errored ? "Failed" : typeLabel(filename)}
          </p>
        </div>
      </div>
      {removeButton}
    </div>
  );
};
